import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase, getHouseholdMemberProfiles } from '../lib/supabase'
import type { DbCoupleInvite } from '../types'

interface CoupleState {
  pendingInvite: DbCoupleInvite | null
  partnerUserId: string | null
  memberCount: number
}

export function useCoupleInvite(userId: string | null | undefined, householdId: string | null | undefined) {
  const [state, setState] = useState<CoupleState>({
    pendingInvite: null,
    partnerUserId: null,
    memberCount: 1,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const suppressRefresh = useRef(false)

  const refresh = useCallback(async () => {
    if (suppressRefresh.current) return
    if (!userId || !householdId) return
    setLoading(true)
    setError(null)
    try {
      const [members, inviteResult] = await Promise.all([
        getHouseholdMemberProfiles(householdId),
        supabase
          .from('couple_invites')
          .select('*')
          .eq('inviter_id', userId)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])

      const partnerUserId = members.find(m => m.user_id !== userId)?.user_id ?? null

      setState({
        pendingInvite: (inviteResult.data as DbCoupleInvite | null),
        partnerUserId,
        memberCount: members.length,
      })
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [userId, householdId])

  useEffect(() => { refresh() }, [refresh])

  // Realtime: watch couple_invites for changes on this user's invites
  useEffect(() => {
    if (!userId || !householdId) return

    const channel = supabase
      .channel(`couple-invite:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'couple_invites',
          filter: `inviter_id=eq.${userId}`,
        },
        () => { refresh() },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'household_members',
          filter: `household_id=eq.${householdId}`,
        },
        () => { refresh() },
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [userId, householdId, refresh])

  async function sendInvite(inviteeEmail: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession()
    const { error: fnErr } = await supabase.functions.invoke('send-couple-invite', {
      body: { inviteeEmail },
      headers: { Authorization: `Bearer ${session?.access_token}` },
    })
    if (fnErr) throw new Error(fnErr.message ?? 'Erro ao enviar convite.')
    await refresh()
  }

  async function cancelInvite(): Promise<void> {
    if (!state.pendingInvite) return
    const { error: upErr } = await supabase
      .from('couple_invites')
      .update({ status: 'expired' })
      .eq('id', state.pendingInvite.id)
    if (upErr) throw new Error(upErr.message)
    await refresh()
  }

  async function resendInvite(inviteeEmail: string): Promise<void> {
    suppressRefresh.current = true
    try {
      if (state.pendingInvite) {
        const { error: upErr } = await supabase
          .from('couple_invites')
          .update({ status: 'expired' })
          .eq('id', state.pendingInvite.id)
        if (upErr) throw new Error(upErr.message)
      }
      const { data: { session } } = await supabase.auth.getSession()
      const { error: fnErr } = await supabase.functions.invoke('send-couple-invite', {
        body: { inviteeEmail },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })
      if (fnErr) throw new Error(fnErr.message ?? 'Erro ao enviar convite.')
    } finally {
      suppressRefresh.current = false
      await refresh()
    }
  }

  async function unlinkCouple(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession()
    const { data, error: fnErr } = await supabase.functions.invoke('unlink-couple', {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    })
    if (fnErr) throw new Error(fnErr.message ?? 'Erro ao desvincular.')
    return (data as { newHouseholdId: string }).newHouseholdId
  }

  return {
    ...state,
    loading,
    error,
    refresh,
    sendInvite,
    cancelInvite,
    resendInvite,
    unlinkCouple,
  }
}
